import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import SocketService from '../../../../../packages/shared/socket';
import { getBackendUrl, getSocketUrl } from '../../config/api';

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.3s ease;
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
`;

const VideoIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const SlideCounter = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-family: 'JetBrains Mono', monospace;
`;

const SpeakerNotes = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 100px;
  background: rgba(0, 0, 0, 0.8);
  color: #ccc;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  max-height: 100px;
  overflow-y: auto;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 18px;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 1.5rem;
  text-align: center;
  padding: 2rem;
`;

/**
 * ControlledSlides - Display slides controlled by Flow Manager
 *
 * Listens to socket events for slide navigation and displays
 * the current slide as an image or video.
 *
 * @param {string} presentationId - Cached presentation ID (UUID or Google ID)
 * @param {string} instanceId - Current instance ID
 * @param {number} initialSlideIndex - Starting slide index (for reconnect scenarios)
 * @param {boolean} showCounter - Show slide counter (default: true)
 * @param {boolean} showNotes - Show speaker notes (default: false, for facilitator view)
 */
const ControlledSlides = ({
  presentationId,
  instanceId,
  initialSlideIndex = 0,
  showCounter = true,
  showNotes = false
}) => {
  const [presentation, setPresentation] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [activePresentationId, setActivePresentationId] = useState(presentationId);
  const [imageRetryCount, setImageRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  const socketUrl = getSocketUrl(); // slide images and videos, not proxied

  // If no presentationId provided, fetch from presentation state
  useEffect(() => {
    if (presentationId || !instanceId) return;

    const fetchState = async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/api/presentations/state/${instanceId}`);
        if (response.ok) {
          const state = await response.json();
          console.log('[ControlledSlides] State response:', state);
          if (state.presentationId) {
            setActivePresentationId(state.presentationId);
            setCurrentSlideIndex(state.currentSlideIndex || 0);
          } else if (!state.active) {
            setLoading(false);
            setError('Ei aktiivista esitystä');
          }
        }
      } catch (err) {
        console.error('[ControlledSlides] Error fetching state:', err);
        setLoading(false);
        setError(err.message);
      }
    };

    fetchState();
  }, [instanceId, presentationId, socketUrl]);

  // Fetch presentation data on mount
  useEffect(() => {
    if (!activePresentationId) return;

    const fetchPresentation = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${getBackendUrl()}/api/presentations/${activePresentationId}`);

        if (!response.ok) {
          throw new Error('Presentation not found');
        }

        const data = await response.json();
        setPresentation(data);
        setError(null);
      } catch (err) {
        console.error('[ControlledSlides] Error fetching presentation:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPresentation();
  }, [activePresentationId, socketUrl]);

  // Listen for slide change events
  useEffect(() => {
    console.log('[ControlledSlides] Setting up socket listener, instanceId:', instanceId);
    if (!instanceId) {
      console.log('[ControlledSlides] No instanceId, skipping socket setup');
      return;
    }

    // Connect to socket for this instance
    const socket = SocketService.connect(instanceId, 'display', `slides-${Date.now()}`);
    console.log('[ControlledSlides] Socket state:', socket?.connected, 'id:', socket?.id);
    if (!socket) return;

    const handleSlideChanged = (data) => {
      console.log('[ControlledSlides] 🎯 Slide changed event received:', data);
      if (data.instanceId === instanceId) {
        console.log('[ControlledSlides] Updating to slide:', data.currentSlideIndex);
        setCurrentSlideIndex(data.currentSlideIndex);
        setImageLoaded(false); // Reset for new slide
        setImageRetryCount(0); // Reset retry count for new slide
      } else {
        console.log('[ControlledSlides] Event for different instance, ignoring');
      }
    };

    const handlePresentationActivated = (data) => {
      console.log('[ControlledSlides] Presentation activated:', data);
      if (data.instanceId === instanceId) {
        // Update active presentation if a new one was activated
        if (data.presentationId) {
          setActivePresentationId(data.presentationId);
        }
        setCurrentSlideIndex(data.currentSlideIndex || 0);
        setImageLoaded(false);
      }
    };

    // Set up event listeners (works even if socket not yet connected)
    socket.on('presentation-slide-changed', handleSlideChanged);
    socket.on('presentation-activated', handlePresentationActivated);

    // If socket connects/reconnects, rejoin the instance room and sync state
    const handleConnect = async () => {
      console.log('[ControlledSlides] Socket connected, joining instance room and syncing state');
      socket.emit('join-session', {
        sessionId: instanceId,
        clientType: 'display',
        participantId: `slides-${Date.now()}`
      });

      // Fetch current state from server to ensure we're synced
      try {
        const response = await fetch(`${getBackendUrl()}/api/presentations/state/${instanceId}`);
        if (response.ok) {
          const state = await response.json();
          console.log('[ControlledSlides] Synced state from server:', state);
          if (state.presentationId && state.presentationId !== activePresentationId) {
            setActivePresentationId(state.presentationId);
          }
          if (typeof state.currentSlideIndex === 'number') {
            setCurrentSlideIndex(state.currentSlideIndex);
            setImageLoaded(false);
            setImageRetryCount(0);
          }
        }
      } catch (err) {
        console.error('[ControlledSlides] Error syncing state:', err);
      }
    };

    // If already connected, join now
    if (socket.connected) {
      handleConnect();
    }
    socket.on('connect', handleConnect);

    return () => {
      socket.off('presentation-slide-changed', handleSlideChanged);
      socket.off('presentation-activated', handlePresentationActivated);
      socket.off('connect', handleConnect);
    };
  }, [instanceId]);

  // Get current slide data
  const currentSlide = presentation?.slides?.[currentSlideIndex];
  const slideCount = presentation?.slide_count || 0;

  // Handle keyboard navigation (for local testing)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        setCurrentSlideIndex(prev => Math.min(prev + 1, slideCount - 1));
        setImageLoaded(false);
      } else if (e.key === 'ArrowLeft') {
        setCurrentSlideIndex(prev => Math.max(prev - 1, 0));
        setImageLoaded(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slideCount]);

  if (loading) {
    return (
      <Container>
        <LoadingOverlay>Ladataan esitystä...</LoadingOverlay>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>
          {error}
          <br />
          <small style={{ opacity: 0.7 }}>ID: {presentationId}</small>
        </ErrorMessage>
      </Container>
    );
  }

  if (!currentSlide) {
    return (
      <Container>
        <ErrorMessage>Diaa ei löydy</ErrorMessage>
      </Container>
    );
  }

  // If slide has video, show video player
  if (currentSlide.hasVideo && currentSlide.video) {
    const isYouTube = currentSlide.video.source === 'YOUTUBE';

    if (isYouTube) {
      // YouTube: use iframe embed with autoplay
      const videoUrl = `${currentSlide.video.embedUrl}?autoplay=1&mute=1`;
      return (
        <Container>
          <VideoContainer>
            <VideoIframe
              src={videoUrl}
              allowFullScreen
              allow="autoplay; fullscreen; encrypted-media"
              title={`Video - Slide ${currentSlideIndex + 1}`}
            />
          </VideoContainer>
          {showCounter && (
            <SlideCounter>
              {currentSlideIndex + 1} / {slideCount}
            </SlideCounter>
          )}
        </Container>
      );
    } else {
      // Google Drive: use video element with backend proxy for autoplay
      const fileId = currentSlide.video.id || currentSlide.video.url?.match(/[-\w]{25,}/)?.[0];
      const videoUrl = fileId
        ? `${socketUrl}/api/presentations/video/${fileId}`
        : currentSlide.video.embedUrl;

      return (
        <Container>
          <VideoContainer>
            <VideoElement
              src={videoUrl}
              autoPlay
              controls
              playsInline
              onError={(e) => console.error('[ControlledSlides] Video error:', e)}
            />
          </VideoContainer>
          {showCounter && (
            <SlideCounter>
              {currentSlideIndex + 1} / {slideCount}
            </SlideCounter>
          )}
        </Container>
      );
    }
  }

  // Use our proxy endpoint which serves from local DB first, then falls back to Google
  // This is more reliable than direct Google URLs which can fail due to auth/rate limits
  const proxyImageUrl = activePresentationId
    ? `${socketUrl}/api/presentations/${activePresentationId}/slides/${currentSlideIndex}/image`
    : null;

  // Handle image error with auto-retry
  const handleImageError = () => {
    console.log(`[ControlledSlides] Image load error, retry ${imageRetryCount + 1}/${MAX_RETRIES}`);
    if (imageRetryCount < MAX_RETRIES) {
      setImageRetryCount(prev => prev + 1);
      // Force re-render with cache-busting query param after delay
      setTimeout(() => {
        setImageLoaded(false);
      }, 1000 * (imageRetryCount + 1)); // Increasing delay: 1s, 2s, 3s
    } else {
      setError('Kuvan lataus epäonnistui');
    }
  };

  // Build image URL with cache-busting on retry
  const imageUrlWithRetry = proxyImageUrl && imageRetryCount > 0
    ? `${proxyImageUrl}?retry=${imageRetryCount}`
    : proxyImageUrl;

  // Otherwise show slide image
  return (
    <Container>
      {!imageLoaded && (
        <LoadingOverlay>Ladataan diaa {currentSlideIndex + 1}...</LoadingOverlay>
      )}
      <SlideImage
        key={`slide-${currentSlideIndex}-retry-${imageRetryCount}`}
        src={imageUrlWithRetry}
        alt={`Slide ${currentSlideIndex + 1}`}
        onLoad={() => setImageLoaded(true)}
        onError={handleImageError}
        style={{ opacity: imageLoaded ? 1 : 0 }}
      />
      {showCounter && (
        <SlideCounter>
          {currentSlideIndex + 1} / {slideCount}
        </SlideCounter>
      )}
      {showNotes && currentSlide.speakerNotes && (
        <SpeakerNotes $visible={true}>
          {currentSlide.speakerNotes}
        </SpeakerNotes>
      )}
    </Container>
  );
};

export default ControlledSlides;
